import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import type {
  ActionJustificationPanelProps,
  JustificationReason,
} from 'shared-libs/components/ui2/fields/field-ui/action-justification-panel/action-justification-panel-props'
import { SelectOption } from 'shared-libs/components/ui2/fields/core-field/selectable-field-props'

import { UI2PickerField } from 'browser/components/ui2/fields/ui2-picker-field/ui2-picker-field'
import { UI2TextAreaField } from 'browser/components/ui2/fields/ui2-text-area-field/ui2-text-area-field'

import './_action-justification-panel.scss'

const OTHER_LABEL = 'Other'

interface IUI2ActionJustificationPanelWebExtras {
  className?: string
}

export interface IUI2ActionJustificationPanelProps
  extends ActionJustificationPanelProps,
    IUI2ActionJustificationPanelWebExtras {}

interface IUI2ActionJustificationPanelState {
  isOtherSelected: boolean
}

export class UI2ActionJustificationPanel extends React.Component<
  IUI2ActionJustificationPanelProps,
  IUI2ActionJustificationPanelState
> {
  public static defaultProps: Partial<IUI2ActionJustificationPanelProps> = {
    label: 'Reason',
    placeholder: 'Enter a reason',
  }

  constructor(props: IUI2ActionJustificationPanelProps) {
    super(props)
    const options = UI2ActionJustificationPanel.normalizeReasons(props.reasons)
    const hasOther = _.some(options, (r) => UI2ActionJustificationPanel.isOtherValue(r.value))
    const isFreeform =
      !_.isEmpty(props.reason) && !_.some(options, (r) => r.value === props.reason)
    this.state = { isOtherSelected: hasOther && isFreeform }
  }

  public render(): React.ReactNode {
    const {
      reasons,
      reason,
      label,
      placeholder,
      isDisabled,
      isPickerRequired,
      isFreeformRequired,
      errors,
      frames,
      translationTable,
      className,
    } = this.props
    const options = UI2ActionJustificationPanel.normalizeReasons(reasons)
    const hasPresetList = !_.isEmpty(options)

    if (!hasPresetList) {
      return (
        <div className={classNames('ui2-actionJustificationPanel', className)}>
          <UI2TextAreaField
            label={label}
            value={reason}
            onChange={this.handleFreeformChange}
            isRequired={isFreeformRequired}
            errors={errors}
            rows={2}
            isDisabled={isDisabled}
            placeholder={placeholder}
            frames={frames}
            translationTable={translationTable}
          />
        </div>
      )
    }

    const isOtherActive = this.state.isOtherSelected
    const selectValue = isOtherActive
      ? UI2ActionJustificationPanel.findOtherValue(options)
      : reason
    const selectOptions: SelectOption[] = options.map((o) => ({ label: o.label, value: o.value }))

    return (
      <div className={classNames('ui2-actionJustificationPanel', className)}>
        <UI2PickerField
          label={label}
          options={selectOptions}
          value={selectValue}
          onChange={this.handleSelectChange}
          isRequired={isPickerRequired}
          errors={isOtherActive ? undefined : errors}
          placeholder='Select a reason'
          isDisabled={isDisabled}
          frames={frames}
          translationTable={translationTable}
        />
        {isOtherActive && (
          <UI2TextAreaField
            label='Other reason'
            value={reason}
            onChange={this.handleFreeformChange}
            isRequired={isFreeformRequired}
            errors={errors}
            rows={2}
            isDisabled={isDisabled}
            placeholder={placeholder}
            frames={frames}
            translationTable={translationTable}
          />
        )}
      </div>
    )
  }

  private handleSelectChange = (raw: any) => {
    const next: string | undefined =
      raw && typeof raw === 'object' && 'value' in raw ? raw.value : raw
    if (UI2ActionJustificationPanel.isOtherValue(next)) {
      this.setState({ isOtherSelected: true })
      this.props.onOtherModeChange?.(true)
      this.props.onReasonChange(undefined)
      return
    }
    if (this.state.isOtherSelected) {
      this.setState({ isOtherSelected: false })
      this.props.onOtherModeChange?.(false)
    }
    this.props.onReasonChange(next)
  }

  private handleFreeformChange = (next: string) => {
    this.props.onReasonChange(next)
  }

  private static normalizeReasons(
    reasons: Array<string | JustificationReason> | undefined,
  ): JustificationReason[] {
    if (!reasons) return []
    return _.map(reasons, (r) => (typeof r === 'string' ? { label: r, value: r } : r))
  }

  private static isOtherValue(value: unknown): boolean {
    return typeof value === 'string' && value.toLowerCase() === OTHER_LABEL.toLowerCase()
  }

  private static findOtherValue(options: JustificationReason[]): string | undefined {
    const match = _.find(options, (o) => UI2ActionJustificationPanel.isOtherValue(o.value))
    return match?.value
  }
}
